import { useEffect, useRef, useState } from 'react';

const CATEGORIES = [
  {
    icon: 'ri-vip-crown-line',
    title: '貴金属・宝飾品',
    description: '金・プラチナ・ダイヤモンド・ルビー・エメラルドなど、ジュエリー全般を高価買取。刻印がなくても査定いたします。',
    image: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/91bf0449f14295c65a0b3ac79152f9f1.webp',
    tags: ['金', 'プラチナ', 'ダイヤモンド', 'ジュエリー'],
  },
  {
    icon: 'ri-shopping-bag-3-line',
    title: 'ブランド品',
    description: 'ヴィトン・エルメス・シャネル・グッチなどのバッグ・財布・アクセサリー・時計。使わないブランド品を高額査定。',
    image: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/86f2ae5b9255bdc48bcef0b2f341bc80.webp',
    tags: ['バッグ', '財布', '時計', 'アクセサリー'],
  },
  {
    icon: 'ri-time-line',
    title: '腕時計',
    description: 'ロレックス・オメガ・セイコー・シチズンなどの国内外ブランド腕時計を高価買取。動作品・不動品どちらも査定いたします。',
    image: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/db332611b80be31af93da09cf546b164.webp',
    tags: ['ロレックス', 'オメガ', 'セイコー', 'ブランド時計'],
  },
  {
    icon: 'ri-tv-2-line',
    title: '家電・デジタル製品',
    description: 'スマートフォン・タブレット・ゲーム機・カメラ・パソコン・生活家電など。壊れていても査定可能です。',
    image: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/6ddb692cdaac23507b26d8aebe48da61.webp',
    tags: ['スマホ', 'カメラ', 'ゲーム機', 'PC'],
  },
  {
    icon: 'ri-sofa-line',
    title: '家具・生活用品',
    description: '家具・食器・調理器具・衣類・寝具など生活全般の用品を買取。引越し時の大量処分もご相談ください。',
    image: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/1a1022d92d162c03489d10bc72ff320c.webp',
    tags: ['家具', '食器', '衣類', '寝具'],
  },
  {
    icon: 'ri-copper-coin-line',
    title: '記念硬貨・古銭・切手',
    description: '記念コイン・古銭・外国コイン・切手・金貨・銀貨など。希少品・コレクション品も丁寧に査定いたします。',
    image: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/650efa2b7b938f51cdaa609ddc2b267d.webp',
    tags: ['古銭', '記念コイン', '切手', '金貨・銀貨'],
  },
];

const CategorySection = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="categories" ref={ref} className="py-20 md:py-28 bg-[#F8F9F7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-[#B2AC88] text-xs font-medium tracking-widest uppercase">Categories</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#2F3630] mt-3 mb-4" style={{ fontFamily: "'Shippori Mincho', serif" }}>
            幅広い品目に対応
          </h2>
          <p className="text-gray-500 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
            岡山市の高価買取専門店として、貴金属から家電・家具まで丁寧に査定いたします。
            まずはお気軽にご相談ください。
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {CATEGORIES.map((cat, i) => (
            <article
              key={cat.title}
              className={`group bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-700 hover:-translate-y-1 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={cat.image}
                  alt={`${cat.title}の買取 岡山市`}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-3 left-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full">
                    <i className={`${cat.icon} text-white text-lg`} />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-5">
                <h3 className="text-[#2F3630] font-bold text-base mb-2">{cat.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed mb-3">{cat.description}</p>
                <div className="flex flex-wrap gap-1.5">
                  {cat.tags.map((tag) => (
                    <span key={tag} className="text-xs bg-[#B2AC88]/10 text-[#B2AC88] px-2 py-0.5 rounded-full font-medium">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className={`text-center mt-12 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-gray-500 text-sm mb-4">掲載品目以外でもお気軽にご相談ください</p>
          <a
            href="tel:086-238-7864"
            className="inline-flex items-center gap-2 bg-[#D97706] hover:bg-[#c06a05] text-white font-medium px-8 py-3 rounded-full transition-colors duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-phone-line" />
            086-238-7864 に電話する
          </a>
        </div>
      </div>
    </section>
  );
};

export default CategorySection;
